/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import { useAuth } from "context/AuthContext";
import { useBroadcast } from "context/BroadcastContext";
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import alertSoundCancel from "assets/sounds/alertCancel.mp3";
import alertSound from "assets/sounds/alert.mp3";
import { handleGetOrdersRequest } from "store/modules/Orders/thunk.store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useOrderAlertSounds } from "./signalR/useOrderAlertSounds";
import { parsePersistedDateFromObject } from "utils/parsePersistedDateFromObject";
import { toast } from "react-toastify";
import i18next from "i18next";
import { EOrderStatusFinally } from "enums/options/orders";
import { OrderProps } from "store/modules/Orders/types.store";
import { Button, Box, Dialog, DialogContent, Typography } from "@mui/material";

interface SignalRProviderProps {
  children: React.ReactNode;
}

interface SignalRContextProps {
  ordersSignal: OrderProps[];
  setOrdersSignal: React.Dispatch<React.SetStateAction<OrderProps[]>>;
  lastOrderSignal: OrderProps;
  setLastOrderSignal: React.Dispatch<
    React.SetStateAction<OrderProps[] | undefined>
  >;
}

const SignalRContext = createContext<SignalRContextProps>(
  {} as SignalRContextProps,
);

const THREE_SECONDS_IN_MS = 3000;

interface DisconnectModalProps {
  open: boolean;
  onClose: () => void;
}

const DisconnectModal: React.FC<DisconnectModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      maxWidth={false}
      onClose={() => {}}
      sx={{ zIndex: 10000 }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <DialogContent
        sx={{
          minWidth: "400px",
          maxWidth: "500px",
          margin: "auto",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography align="center" gutterBottom variant="body1">
            O Portal da Revenda já está aberto em outra janela. Para continuar
            clique em &quot;Usar nesta janela&quot;. Caso prefira, feche esta
            janela para seguir utilizando a anterior.
          </Typography>
          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                minWidth: 120,
                backgroundColor: "#D33100",
                "&:hover": {
                  backgroundColor: "#D33100",
                },
              }}
            >
              Usar nesta janela
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export const SignalRProvider = ({ children }: SignalRProviderProps) => {
  const dispatch = useAppDispatch();
  const { dealerId } = useAuth();
  const { allOrders } = useAppSelector((state) => state.orders);
  const [lastOrderSignal, setLastOrderSignal] = useState<OrderProps | any>();
  const [ordersSignal, setOrdersSignal] = useState<OrderProps[] | any>();
  const [isCanceledOrders, setIsCanceledOrders] = useState<boolean>(false);
  const [cancellationRingNumber, setCancellationRingNumber] =
    useState<number>(0);
  const [newOrdersId, setNewOrdersId] = useState<string[]>([]);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const { playNewOrderSound, playCancelSound } = useOrderAlertSounds(
    alertSound,
    alertSoundCancel,
    dealerId,
  );

  const isMountedRef = useRef(true);
  const isInitializingRef = useRef(false);
  const hubConnection = useRef<HubConnection | null>(null);
  const tabId = useRef(crypto.randomUUID());
  const shouldForceCloseConnection = useRef(false);
  const processedCancelToastIdsRef = useRef<Set<string>>(new Set());
  const { channel } = useBroadcast();

  useEffect(() => {
    const soundTimer = setInterval(() => {
      if (dealerId && isCanceledOrders && cancellationRingNumber < 3) {
        playCancelSound();
        setCancellationRingNumber(cancellationRingNumber + 1);
      } else {
        setCancellationRingNumber(0);
        setIsCanceledOrders(false);
      }
    }, 2000);

    return () => {
      clearInterval(soundTimer);
    };
  }, [dealerId, cancellationRingNumber, isCanceledOrders, playCancelSound]);

  useEffect(() => {
    const allOrdersLength = allOrders.length;
    if (!allOrdersLength) return;
    const orders = [...allOrders].reverse();
    setOrdersSignal(orders);
  }, [allOrders]);

  const functionCanceledOrders = (data: OrderProps) => {
    const isCanceled =
      data.status === EOrderStatusFinally.DEALERCANCELLED ||
      data.status === EOrderStatusFinally.DEALERDENIED ||
      data.status === EOrderStatusFinally.TIMEOUTCANCELLED ||
      data.status === EOrderStatusFinally.CONSUMERCANCELLED;

    if (
      isCanceled &&
      !isCanceledOrders &&
      !processedCancelToastIdsRef.current.has(data.id)
    ) {
      processedCancelToastIdsRef.current.add(data.id);
      setIsCanceledOrders(true);

      switch (data.status) {
        case EOrderStatusFinally.DEALERCANCELLED:
        case EOrderStatusFinally.DEALERDENIED:
          toast(
            i18next.t("Um pedido foi cancelado: pela revenda.").toString(),
            {
              type: "error",
              toastId: data.id,
            },
          );
          break;

        case EOrderStatusFinally.TIMEOUTCANCELLED:
          toast(
            i18next.t("Um pedido foi cancelado: não foi aceito.").toString(),
            {
              type: "error",
              toastId: data.id,
            },
          );
          break;

        case EOrderStatusFinally.CONSUMERCANCELLED:
          toast(
            i18next.t("Um pedido foi cancelado: pelo usuário.").toString(),
            {
              type: "error",
              toastId: data.id,
            },
          );
          break;

        default:
          break;
      }
    }
  };

  const handleOrderAlert = (
    id: string,
    dealerState: string,
    paymentType: string,
    paymentStatus: string,
  ) => {
    if (
      dealerState === "New" &&
      ((paymentType === "INAPP" && paymentStatus === "Payed") ||
        paymentType === "ONDELIVERY")
    ) {
      setNewOrdersId((prevState) => [...prevState, id]);
    } else {
      if (!newOrdersId.length) {
        return;
      }
      setNewOrdersId((prevState) => {
        const filteredNewOrders = prevState.filter((orderId) => orderId !== id);
        return filteredNewOrders;
      });
    }
  };

  const closeConnection = useCallback(async () => {
    if (
      hubConnection.current &&
      hubConnection.current.state === HubConnectionState.Connected
    ) {
      try {
        console.log("fechando conexão dos pedidos");

        hubConnection.current.off("OrderChanged");

        await hubConnection.current.stop();
      } catch (error) {
        console.error("error ao fechar a conexão", error);
      } finally {
        hubConnection.current = null;
      }
    }
  }, []);

  const initializeConnection = useCallback(async () => {
    if (isInitializingRef.current || !isMountedRef.current || !dealerId) {
      return;
    }

    isInitializingRef.current = true;

    try {
      await closeConnection();

      console.log("inicializando conexão dos pedidos");

      hubConnection.current = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_ORDERS_API_URL}order-hubs/orders`, {
          withCredentials: false,
        })
        .configureLogging(
          process.env.NODE_ENV === "development"
            ? LogLevel.Information
            : LogLevel.Error,
        )
        .withAutomaticReconnect([
          0, 3000, 5000, 10000, 15000, 30000, 60000, 120000,
        ])
        .build();

      hubConnection.current.onreconnected(async () => {
        console.log("conexão restabelecida");

        if (
          isMountedRef.current &&
          hubConnection.current &&
          hubConnection.current.state === HubConnectionState.Connected
        ) {
          await hubConnection.current.invoke(
            "SubscribeForNotifications",
            dealerId,
          );
          console.log("Obtendo pedidos via API...");
          dispatch(handleGetOrdersRequest({ dealerId }));
        }
      });

      hubConnection.current.onclose(async () => {
        console.log("conexão fechada de pedidos");

        if (isMountedRef.current && !shouldForceCloseConnection.current) {
          await initializeConnection();
        }
      });

      hubConnection.current.on("OrderChanged", (data: OrderProps) => {
        parsePersistedDateFromObject(data, "createdAt");
        setLastOrderSignal(data);
        functionCanceledOrders(data);

        handleOrderAlert(
          data.id,
          data.dealerState,
          data.payment.type,
          data.paymentStatus as string,
        );
      });

      await hubConnection.current.start();
      console.log("conexão dos pedidos iniciada");

      if (
        isMountedRef.current &&
        !shouldForceCloseConnection.current &&
        hubConnection.current &&
        hubConnection.current.state === HubConnectionState.Connected
      ) {
        await hubConnection.current.invoke(
          "SubscribeForNotifications",
          dealerId,
        );
        console.log("Obtendo pedidos via API...");
        dispatch(handleGetOrdersRequest({ dealerId }));
      }
    } catch (error) {
      console.log("error ao iniciar o SignalR:", error);

      const retryDelay = Math.min(5000 * 2 ** Math.random(), 30000);

      setTimeout(() => {
        if (isMountedRef.current && !shouldForceCloseConnection.current) {
          initializeConnection();
        }
      }, retryDelay);
      return;
    } finally {
      isInitializingRef.current = false;

      if (channel.current) {
        channel.current.postMessage({
          type: "SIGNALR_CONNECTION",
          fromTabId: tabId.current,
        });
      }
    }
  }, [dealerId, closeConnection]);

  useEffect(() => {
    if (!lastOrderSignal) {
      return;
    }

    setOrdersSignal((currentOrders: OrderProps[] | undefined) => {
      if (!currentOrders?.length) {
        return [lastOrderSignal];
      }

      const index = currentOrders.findIndex(
        (order) => order.id === lastOrderSignal.id,
      );
      const newArray = [...currentOrders];

      if (index >= 0) {
        newArray[index] = lastOrderSignal;
      } else {
        newArray.push(lastOrderSignal);
      }

      return newArray;
    });

    setLastOrderSignal(undefined);
  }, [lastOrderSignal]);

  useEffect(() => {
    if (shouldForceCloseConnection.current) return () => {};

    isMountedRef.current = true;
    initializeConnection();

    return () => {
      isMountedRef.current = false;
      closeConnection();
    };
  }, [dealerId]);

  useEffect(() => {
    const soundTimer = setInterval(() => {
      const hasNewOrders = ordersSignal?.find(
        (order: OrderProps) => order.dealerState === "New",
      );

      if (hasNewOrders && dealerId && !isCanceledOrders) {
        playNewOrderSound();
      }
    }, THREE_SECONDS_IN_MS);

    return () => {
      clearInterval(soundTimer);
    };
  }, [ordersSignal, dealerId, isCanceledOrders, playNewOrderSound]);

  useEffect(() => {
    channel.current.onmessage = (event) => {
      const { type, fromTabId } = event.data;

      if (!hubConnection.current) {
        return;
      }

      if (type === "SIGNALR_CONNECTION" && fromTabId !== tabId.current) {
        if (
          hubConnection.current &&
          hubConnection.current.state === HubConnectionState.Connected
        ) {
          console.log(
            "Nova aba conectou, fechando conexão atual e exibindo modal.",
          );
          shouldForceCloseConnection.current = true;
          setShowDisconnectModal(true);

          window.dispatchEvent(
            new CustomEvent("CLOSE_CHAT_CONNECTION", {
              detail: { fromTabId },
            }),
          );

          setTimeout(() => {
            closeConnection();
          }, 50);
        }
      }
    };
  }, [closeConnection]);

  const handleDisconnectModalClose = async () => {
    setShowDisconnectModal(false);

    shouldForceCloseConnection.current = false;

    console.log("Disparando evento para reconexão do chat.");
    window.dispatchEvent(
      new CustomEvent("RESTORE_CHAT_CONNECTION", {
        detail: { fromTabId: tabId.current },
      }),
    );

    await initializeConnection();
  };

  useEffect(() => {
    if (shouldForceCloseConnection.current) {
      const resetTimer = setTimeout(() => {
        shouldForceCloseConnection.current = false;
      }, 5000);

      return () => {
        clearTimeout(resetTimer);
      };
    }
    return () => {};
  }, [shouldForceCloseConnection]);

  return (
    <SignalRContext.Provider
      value={{
        ordersSignal,
        setOrdersSignal,
        lastOrderSignal,
        setLastOrderSignal,
      }}
    >
      <DisconnectModal
        open={showDisconnectModal}
        onClose={handleDisconnectModalClose}
      />
      {children}
    </SignalRContext.Provider>
  );
};

export function useSignalR(): SignalRContextProps {
  const context = useContext(SignalRContext);

  if (!context) {
    throw new Error("useSignalR must be used within a SignalRContext");
  }
  return context;
}
